/* eslint-disable security/detect-object-injection */

import {
  BenchmarkType,
  ConservationBenchmark,
  IndicatorFactType,
  PlotReportFactElement,
  PlotReportFactLabelNameEnum,
  PopulationDensityBenchmark,
  R1FactType,
} from '@/api/rest/resources/types/fact';
import { GraphFact } from '@/components/Charts/types';
import { getFactFromFactArray } from '@/pages/shared/hooks/utils';
import { isNullish } from '@/utils/booleans';

import { useOrganizationIndicatorGraphFact, useOrganizationIndicatorReportingFact } from './useOrganizationFact';
import { usePlotFacts } from './usePlotFact';

export const useBenchmarksForOrganization = (
  benchmarkType: IndicatorFactType,
  organizationId?: string | null,
  formatter?: (
    fact: GraphFact | undefined | PlotReportFactElement<number | null>,
  ) => number | null | undefined | { value: number | undefined; benchmark: BenchmarkType | undefined },
  plotGroupId?: string | null,
) => {
  const { [benchmarkType]: lowBenchmarkData } = useOrganizationIndicatorGraphFact<GraphFact[] | number>(
    benchmarkType,
    organizationId,
    {
      [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.low,
    },
    plotGroupId,
  );

  const { [benchmarkType]: moderateBenchmarkData } = useOrganizationIndicatorGraphFact<GraphFact[] | number>(
    benchmarkType,
    organizationId,
    {
      [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.moderate,
    },
    plotGroupId,
  );

  const { [benchmarkType]: highBenchmarkData } = useOrganizationIndicatorGraphFact<GraphFact[] | number>(
    benchmarkType,
    organizationId,
    {
      [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.high,
    },
    plotGroupId,
  );

  const lowBenchmarkDataFact = Array.isArray(lowBenchmarkData?.value)
    ? (lowBenchmarkData?.value as GraphFact[]).at(-1)
    : (lowBenchmarkData as PlotReportFactElement<number | null>);
  const moderateBenchmarkDataFact = Array.isArray(moderateBenchmarkData?.value)
    ? (moderateBenchmarkData?.value as GraphFact[]).at(-1)
    : (moderateBenchmarkData as PlotReportFactElement<number | null>);
  const highBenchmarkDataFact = Array.isArray(highBenchmarkData?.value)
    ? (highBenchmarkData?.value as GraphFact[]).at(-1)
    : (highBenchmarkData as PlotReportFactElement<number | null>);

  const lowBenchmark = formatter?.(lowBenchmarkDataFact) ?? lowBenchmarkDataFact?.value;
  const moderateBenchmark = formatter?.(moderateBenchmarkDataFact) ?? moderateBenchmarkDataFact?.value;
  const highBenchmark = formatter?.(highBenchmarkDataFact) ?? highBenchmarkDataFact?.value;

  return [lowBenchmark, moderateBenchmark, highBenchmark];
};

export const useBenchmarkIndicatorsForOrganization = (
  benchmarkType: IndicatorFactType,
  organizationId?: string | null,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formatter?: (fact: any) => number | null | undefined,
) => {
  const { [benchmarkType]: lowBenchmarkData } = useOrganizationIndicatorReportingFact(benchmarkType, organizationId, {
    [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.low,
  });

  const { [benchmarkType]: moderateBenchmarkData } = useOrganizationIndicatorReportingFact(
    benchmarkType,
    organizationId,
    {
      [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.moderate,
    },
  );

  const { [benchmarkType]: highBenchmarkData } = useOrganizationIndicatorReportingFact(benchmarkType, organizationId, {
    [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.high,
  });

  const lowBenchmark = formatter?.(lowBenchmarkData) ?? lowBenchmarkData?.value;
  const moderateBenchmark = formatter?.(moderateBenchmarkData) ?? moderateBenchmarkData?.value;
  const highBenchmark = formatter?.(highBenchmarkData) ?? highBenchmarkData?.value;

  return [lowBenchmark, moderateBenchmark, highBenchmark];
};

export const useBenchmarksForPlot = (
  benchmarkType: IndicatorFactType,
  plotId?: string | null,
  formatter?: (
    fact: GraphFact | undefined,
  ) => number | null | undefined | { value: number | undefined; benchmark: BenchmarkType | undefined },
) => {
  const benchmarks = usePlotFacts<GraphFact[] | number>([benchmarkType], plotId);
  const lowBenchmarkFact = getFactFromFactArray<GraphFact[] | number>(benchmarks, benchmarkType, {
    [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.low,
  });

  const moderateBenchmarkFact = getFactFromFactArray<GraphFact[] | number>(benchmarks, benchmarkType, {
    [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.moderate,
  });

  const highBenchmarkFact = getFactFromFactArray<GraphFact[] | number>(benchmarks, benchmarkType, {
    [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.high,
  });

  return getBenchmarksFromFacts({
    highBenchmarkFact,
    moderateBenchmarkFact,
    lowBenchmarkFact,
    formatter,
  });
};

export const getBenchmarksFromFacts = ({
  highBenchmarkFact,
  moderateBenchmarkFact,
  lowBenchmarkFact,
  veryLowBenchmarkFact,
  unchangedBenchmarkFact,
  formatter,
}: {
  highBenchmarkFact: PlotReportFactElement<GraphFact[] | null | number> | GraphFact | undefined;
  moderateBenchmarkFact: PlotReportFactElement<GraphFact[] | null | number> | GraphFact | undefined;
  lowBenchmarkFact: PlotReportFactElement<GraphFact[] | null | number> | GraphFact | undefined;
  veryLowBenchmarkFact?: PlotReportFactElement<GraphFact[] | null | number> | GraphFact | undefined;
  unchangedBenchmarkFact?: PlotReportFactElement<GraphFact[] | null | number> | GraphFact | undefined;
  formatter?: (
    fact: GraphFact | undefined,
  ) => number | null | undefined | { value: number | undefined; benchmark: BenchmarkType | undefined };
}) => {
  const unchangedBenchmarkData = Array.isArray(unchangedBenchmarkFact?.value)
    ? unchangedBenchmarkFact?.value?.at(-1)
    : unchangedBenchmarkFact;
  const veryLowBenchmarkData = Array.isArray(veryLowBenchmarkFact?.value)
    ? veryLowBenchmarkFact?.value?.at(-1)
    : veryLowBenchmarkFact;
  const lowBenchmarkData = Array.isArray(lowBenchmarkFact?.value) ? lowBenchmarkFact?.value?.at(-1) : lowBenchmarkFact;
  const moderateBenchmarkData = Array.isArray(moderateBenchmarkFact?.value)
    ? moderateBenchmarkFact?.value?.at(-1)
    : moderateBenchmarkFact;
  const highBenchmarkData = Array.isArray(highBenchmarkFact?.value)
    ? highBenchmarkFact?.value?.at(-1)
    : highBenchmarkFact;

  const unchangedBenchmark = formatter?.(unchangedBenchmarkData) ?? unchangedBenchmarkData?.value;
  const veryLowBenchmark = formatter?.(veryLowBenchmarkData) ?? veryLowBenchmarkData?.value;
  const lowBenchmark = formatter?.(lowBenchmarkData) ?? lowBenchmarkData?.value;
  const moderateBenchmark = formatter?.(moderateBenchmarkData) ?? moderateBenchmarkData?.value;
  const highBenchmark = formatter?.(highBenchmarkData) ?? highBenchmarkData?.value;

  let benchmarks = [lowBenchmark, moderateBenchmark, highBenchmark];

  if (!isNullish(veryLowBenchmark)) {
    benchmarks = [veryLowBenchmark, ...benchmarks];
  }

  if (!isNullish(unchangedBenchmark)) {
    benchmarks = [unchangedBenchmark, ...benchmarks];
  }

  return benchmarks;
};

const getClosestBenchmark = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  benchmarks: any[] | null | undefined,
  fact: string | number | null | undefined | unknown,
) => {
  if (isNullish(fact) || !benchmarks || benchmarks.length === 0 || benchmarks.some((bm) => isNullish(bm))) {
    return null;
  }

  const factValue = Number(fact);

  const differences = benchmarks.map((benchmark) => Math.abs(factValue - benchmark));
  const minIndex = differences.indexOf(Math.min(...differences));
  return benchmarks[minIndex];
};

export const useBenchmarkForFact = <T extends BenchmarkType | PopulationDensityBenchmark = BenchmarkType>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  benchmarks: any[] | null | undefined,
  fact: string | number | null | undefined | unknown,
  benchmarkTypes: BenchmarkType[] | PopulationDensityBenchmark[] = [
    BenchmarkType.low,
    BenchmarkType.moderate,
    BenchmarkType.high,
  ],
): T | null => {
  const benchmark = getClosestBenchmark(benchmarks as number[], fact);
  const index = benchmarks?.indexOf(benchmark);

  if (isNullish(benchmark) || isNullish(index)) {
    return null;
  }

  return (benchmarkTypes?.[index] as T) ?? null;
};

export const useConservationBenchmarksForOrganization = (
  fact: R1FactType,
  plotGroupId?: string,
): Record<ConservationBenchmark, number | undefined> => {
  const { [fact]: pristineBenchmark } = useOrganizationIndicatorReportingFact<number>(
    fact,
    null,
    {
      [PlotReportFactLabelNameEnum.benchmark_conservation]: ConservationBenchmark.pristine,
    },
    plotGroupId,
  );

  const { [fact]: goodBenchmark } = useOrganizationIndicatorReportingFact<number>(
    fact,
    null,
    {
      [PlotReportFactLabelNameEnum.benchmark_conservation]: ConservationBenchmark.good,
    },
    plotGroupId,
  );

  const { [fact]: fairBenchmark } = useOrganizationIndicatorReportingFact<number>(
    fact,
    null,
    {
      [PlotReportFactLabelNameEnum.benchmark_conservation]: ConservationBenchmark.fair,
    },
    plotGroupId,
  );

  const { [fact]: poorBenchmark } = useOrganizationIndicatorReportingFact<number>(
    fact,
    null,
    {
      [PlotReportFactLabelNameEnum.benchmark_conservation]: ConservationBenchmark.poor,
    },
    plotGroupId,
  );

  const { [fact]: criticalBenchmark } = useOrganizationIndicatorReportingFact<number>(
    fact,
    null,
    {
      [PlotReportFactLabelNameEnum.benchmark_conservation]: ConservationBenchmark.critical,
    },
    plotGroupId,
  );

  return {
    [ConservationBenchmark.pristine]: pristineBenchmark?.value ?? undefined,
    [ConservationBenchmark.good]: goodBenchmark?.value ?? undefined,
    [ConservationBenchmark.fair]: fairBenchmark?.value ?? undefined,
    [ConservationBenchmark.poor]: poorBenchmark?.value ?? undefined,
    [ConservationBenchmark.critical]: criticalBenchmark?.value ?? undefined,
  };
};

export const getBenchmarkForConservationFromFacts = ({
  facts,
  factType,
}: {
  facts: PlotReportFactElement<unknown>[] | undefined;
  factType: IndicatorFactType;
}) => {
  if (isNullish(facts)) {
    return undefined;
  }

  const pristineBenchmarkFact = getFactFromFactArray<number>(facts, factType, {
    [PlotReportFactLabelNameEnum.benchmark_conservation]: ConservationBenchmark.pristine,
  });
  const goodBenchmarkFact = getFactFromFactArray<number>(facts, factType, {
    [PlotReportFactLabelNameEnum.benchmark_conservation]: ConservationBenchmark.good,
  });
  const fairBenchmarkFact = getFactFromFactArray<number>(facts, factType, {
    [PlotReportFactLabelNameEnum.benchmark_conservation]: ConservationBenchmark.fair,
  });
  const poorBenchmarkFact = getFactFromFactArray<number>(facts, factType, {
    [PlotReportFactLabelNameEnum.benchmark_conservation]: ConservationBenchmark.poor,
  });
  const criticalBenchmarkFact = getFactFromFactArray<number>(facts, factType, {
    [PlotReportFactLabelNameEnum.benchmark_conservation]: ConservationBenchmark.critical,
  });

  return {
    [ConservationBenchmark.pristine]: getValueFromBenchmarkFact(pristineBenchmarkFact),
    [ConservationBenchmark.good]: getValueFromBenchmarkFact(goodBenchmarkFact),
    [ConservationBenchmark.fair]: getValueFromBenchmarkFact(fairBenchmarkFact),
    [ConservationBenchmark.poor]: getValueFromBenchmarkFact(poorBenchmarkFact),
    [ConservationBenchmark.critical]: getValueFromBenchmarkFact(criticalBenchmarkFact),
  };
};

export const getBenchmarkForConservationFact = ({
  fact,
  benchmarks,
}: {
  fact: PlotReportFactElement<GraphFact[] | number | null> | { value: number | null } | undefined;
  benchmarks: Record<ConservationBenchmark, number | undefined> | undefined;
}): ConservationBenchmark | null => {
  if (isNullish(fact) || isNullish(fact.value) || isNullish(benchmarks)) {
    return null;
  }

  const factValue = Number(fact.value);

  if (benchmarks.pristine && factValue >= benchmarks.pristine) {
    return ConservationBenchmark.pristine;
  }

  if (benchmarks.good && factValue >= benchmarks.good) {
    return ConservationBenchmark.good;
  }

  if (benchmarks.fair && factValue >= benchmarks.fair) {
    return ConservationBenchmark.fair;
  }

  if (benchmarks.poor && factValue >= benchmarks.poor) {
    return ConservationBenchmark.poor;
  }

  if (benchmarks.critical && factValue >= benchmarks.critical) {
    return ConservationBenchmark.critical;
  }

  return null;
};

const getValueFromBenchmarkFact = (
  benchmarkFact: PlotReportFactElement<number | null> | undefined,
): number | undefined => {
  if (isNullish(benchmarkFact) || isNullish(benchmarkFact.value)) {
    return undefined;
  }
  return benchmarkFact.value ?? undefined;
};
